import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { Subscription, timer, of, forkJoin } from 'rxjs';
import { switchMap, catchError, timeout } from 'rxjs/operators';
import { Router,
  // import as RouterEvent to avoid confusion with the DOM Event
  Event as RouterEvent,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { LoaderService } from '@core/services/loader.service';
import { AuthStatusService } from '@core/services/auth-status.service';
import { AuthorisedSideNavService } from './core/services/authorised-side-nav.service';
import { SystemInfoService } from '@core/services/system-info.service';
import { SystemStatusService, SYSTEM_STATUS_REQUEST_TIMEOUT } from '@core/services/system-status.service';
import { environment } from '@env/environment';

const SYSTEM_STATE_CHECK_INTERVAL = 30000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['../../node_modules/bootstrap/scss/bootstrap.scss',
    './app.component.scss'],
  host: {
    "(window:click)": "onClick($event)"
  }
})
export class AppComponent implements OnInit, OnDestroy {

  isLoggedIn: boolean;
  userRole: string;
  userName: string;
  systemLocked = false;
  systemLockKnown = false;      // false until the first successful lock check
  systemErrorsCount = 0;        // same count as the dashboard "system status" (0 = usable)
  systemStateChecked = false;   // false until the first check has finished
  lftMode = environment.CERTIFIED_DISPLAY_ENABLED === "true";   // Legal for Trade (OIML)
  private systemStateSubscription: Subscription;


  constructor(
    private titleService: Title,
    private ngZone: NgZone,
    private router: Router,
    private loaderService: LoaderService,
    private authStatusService: AuthStatusService,
    public sideNavService: AuthorisedSideNavService,
    private systemInfoService: SystemInfoService,
    private systemStatusService: SystemStatusService
  ) {
    router.events.subscribe((event: RouterEvent) => {
      this.navigationInterceptor(event);
    });
    this.initalizeMetaTag();
  }

  // Shows and hides the loading spinner during RouterEvent changes
  private navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      // Run this function outside of Angular's zone to bypass change detection
      this.ngZone.runOutsideAngular(() => {
        this.loaderService.display(true);
      });
    }
    if (event instanceof NavigationEnd) {
      this.loaderService.display(false);
    }
    // Set loading state to false in both of the below events to
    // hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.loaderService.display(false);
    }
    if (event instanceof NavigationError) {
      this.loaderService.display(false);
    }
  }

  initalizeMetaTag() {
    this.titleService.setTitle('Cargo Eye');
  }

  checkIsLogIn() {
    this.authStatusService.loggedIn.subscribe(
      (response: boolean) => {
        // Quick fix to resolve navbar issue
        this.isLoggedIn = true;
        const session = window.localStorage.getItem('userDetails');
        if (session) {
          const userDetails = JSON.parse(JSON.stringify(session));
          this.userName = userDetails.username;
          this.userRole = userDetails.roleCode;
          //this.userRole = userDetails.roleCode[0];

        } else {
          this.isLoggedIn = false;
        }
      },
      error => { }
    );
  }

  ngOnInit() {
    this.checkIsLogIn();
    this.watchSystemState();
  }

  ngOnDestroy() {
    if (this.systemStateSubscription) {
      this.systemStateSubscription.unsubscribe();
    }
  }

  // Poll the locked state and the overall health so they are visible on every page,
  // not only when a scan is rejected. Every request times out and every failure is caught,
  // so each round always completes and polling never stops. A failed lock check keeps the
  // last known lock state.
  watchSystemState() {
    this.systemStateSubscription = timer(0, SYSTEM_STATE_CHECK_INTERVAL).pipe(
      switchMap(() => forkJoin([
        this.systemInfoService.getLockedState().pipe(
          timeout(SYSTEM_STATUS_REQUEST_TIMEOUT),
          catchError(() => of(null))
        ),
        this.systemStatusService.getErrorsCount()
      ]))
    ).subscribe((results: any[]) => {
      const lockRes = results[0];
      if (lockRes && lockRes.response) {
        this.systemLocked = lockRes.response.locked === true;
        this.systemLockKnown = true;
      }
      this.systemErrorsCount = results[1];
      this.systemStateChecked = true;
    });
  }

  // unknown (first check still running) > locked > unavailable > available.
  // After the first check, anything not confirmed healthy shows as unavailable (red):
  // a component down (dashboard "system unusable") or the lock state unreadable.
  get systemState(): string {
    if (!this.systemStateChecked) {
      return 'unknown';
    }
    if (this.systemLockKnown && this.systemLocked) {
      return 'locked';
    }
    if (this.systemErrorsCount > 0 || !this.systemLockKnown) {
      return 'unavailable';
    }
    return 'available';
  }

  get systemStateTitle(): string {
    switch (this.systemState) {
      case 'locked':
        return 'System is locked, scans are blocked';
      case 'unavailable':
        return this.systemErrorsCount > 0
          ? 'One or more components are down, see system health'
          : 'Unable to read the system lock state';
      case 'available':
        return 'All components healthy';
      default:
        return 'Checking system state...';
    }
  }

  onClick(event) {
    if(event.target.id !='sideNav')
    this.sideNavService.hideSideNav=true;

  }
}
